import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Skill, CreateSkillDto, UpdateSkillDto } from '../types/skill';

export function useSkills(projectId?: string) {
  return useQuery<Skill[]>({
    queryKey: ['skills', { projectId }],
    queryFn: async () => {
      const params = projectId ? `?projectId=${projectId}` : '';
      const res = await api.get(`/api/skills${params}`);
      return res.data;
    },
  });
}

export function useSkill(id: string | undefined, projectId?: string) {
  return useQuery<Skill>({
    queryKey: ['skills', id, { projectId }],
    queryFn: async () => {
      const params = projectId ? `?projectId=${projectId}` : '';
      const res = await api.get(`/api/skills/${id}${params}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateSkill(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSkillDto) => {
      const params = projectId ? `?projectId=${projectId}` : '';
      const res = await api.post(`/api/skills${params}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}

export function useUpdateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateSkillDto) => {
      const res = await api.put(`/api/skills/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}

export function useDeleteSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/skills/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}
